import { NsKey } from "enums/NsKey";
import { SubsidiaryFormField } from "features/new-actor/types/optim/subsidiaryFormValues";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { Lock } from "lucide-react";
import { FC, ReactNode } from "react";
import { formatSiret } from "shared/utils/formatSiret";
import { LP_BORDER, LP_SURFACE, LP_TEXT } from "./legalProtectionTheme";

type RenderField = (fieldName: SubsidiaryFormField) => ReactNode;

/**
 * Arrangement of the subsidiary form in the side panel: the name, then the SIRET, each on its own
 * line, then the figures on two columns. The inputs are the form's own. `hideIdentity` leaves the
 * two identity fields out when they are shown read-only above (row attached to an existing company).
 */
export const renderSubsidiaryPanelLayout = (hideIdentity: boolean) => (renderField: RenderField) => (
  <div className="flex flex-col gap-5">
    {!hideIdentity && (
      <>
        <div>{renderField('companyName')}</div>
        <div>{renderField('registrationNumber')}</div>
      </>
    )}
    {/* Two columns: a third of the panel is too narrow for these labels and placeholders. */}
    <div className="grid grid-cols-2 gap-x-4 gap-y-5">
      <div>{renderField('revenue')}</div>
      <div>{renderField('employeesCount')}</div>
      <div>{renderField('fiscalYearEnd')}</div>
    </div>
  </div>
);

interface InheritedIdentityBlockProps {
  companyName: string;
  registrationNumber: string;
}

/** Identity taken from an existing company: shown read-only, once, above the figures. */
export const InheritedIdentityBlock: FC<InheritedIdentityBlockProps> = ({ companyName, registrationNumber }) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);

  const renderValue = (label: string, value: string, tabularNums = false) => (
    <div className="flex flex-col gap-1">
      <span className={`text-[13px] font-bold ${LP_TEXT.secondary}`}>{label}</span>
      <span className={`text-[15px] font-bold ${LP_TEXT.title} ${tabularNums ? 'tabular-nums' : ''}`}>{value}</span>
    </div>
  );

  return (
    <div className={`flex flex-col gap-3 rounded-xl border p-5 ${LP_BORDER.card} ${LP_SURFACE.disabled}`}>
      {renderValue(t("ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.LABELS.COMPANY_NAME"), companyName)}
      {renderValue(
        t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.LABELS.REGISTRATION_NUMBER"),
        formatSiret(registrationNumber),
        true
      )}
      <span className={`inline-flex items-center gap-1.5 text-[12px] ${LP_TEXT.secondary}`}>
        <Lock className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.PANEL.INHERITED")}
      </span>
    </div>
  );
};
